<script setup lang="ts">
import type { ApexOptions } from 'apexcharts'
import { nextTick, onMounted, ref, watch } from 'vue'
import ApexCharts from 'vue3-apexcharts'
import {
  getGrowthChartDataAction,
  getGrowthChartDateRangeAction,
  getGrowthChartTitlesAction,
} from '@/api/functions/growth/growth-chart.actions'
import { useToast } from '@/composables/useToast'
import { getErrorMessage } from '@/utils/errorUtils'

const toast = useToast()
const selectedYear = ref<string | null>('all')
const selectedMonth = ref<string | null>('all')
const chartReady = ref(false)
const loading = ref(false)
const series = ref<any[]>([])
const chartWidth = ref('100%')
const apexChart = ref<any>(null)

const startYear = ref<string | null>(null)
const startMonth = ref<string | null>(null)
const startDay = ref<string | null>(null)
const currentYear = ref<string | null>(null)
const currentMonth = ref<string | null>(null)
const currentDay = ref<string | null>(null)

const monthOptions = [
  { label: 'All', value: 'all' },
  { label: 'فروردین', value: '1' },
  { label: 'اردیبهشت', value: '2' },
  { label: 'خرداد', value: '3' },
  { label: 'تیر', value: '4' },
  { label: 'مرداد', value: '5' },
  { label: 'شهریور', value: '6' },
  { label: 'مهر', value: '7' },
  { label: 'آبان', value: '8' },
  { label: 'آذر', value: '9' },
  { label: 'دی', value: '10' },
  { label: 'بهمن', value: '11' },
  { label: 'اسفند', value: '12' },
]

const yearOptions = ref([
  { label: 'سال را انتخاب کنید', value: null },
  { label: 'All', value: 'all' },
  { label: '1397', value: '1397' },
  { label: '1398', value: '1398' },
  { label: '1399', value: '1399' },
  { label: '1400', value: '1400' },
  { label: '1401', value: '1401' },
  { label: '1402', value: '1402' },
  { label: '1403', value: '1403' },
  { label: '1404', value: '1404' },
  { label: '1405', value: '1405' },
])

const chartOptions = ref<ApexOptions>({
  chart: {
    id: 'growth-chart',
    type: 'line',
    zoom: { enabled: true },
  },
  stroke: {
    curve: 'straight',
  },
  dataLabels: {
    enabled: true,
  },
  grid: {
    row: {
      colors: ['#f3f3f3', 'transparent'],
      opacity: 0.5,
    },
  },
  xaxis: {
    categories: ['فروردین', 'اردیبهشت', 'خرداد', 'تیر', 'مرداد', 'شهریور', 'مهر', 'آبان', 'آذر', 'دی', 'بهمن', 'اسفند'],
  },
  yaxis: {
    min: 0,
    tickAmount: 10,
    labels: {
      style: { fontSize: '12px' },
      formatter: (val) => {
        if (val === undefined || val === null || isNaN(val)) return '0'
        if (val >= 1000000) {
          return (val / 1000000).toFixed(2) + 'M'
        } else if (val >= 1000) {
          return (val / 1000).toFixed(2) + 'K'
        }
        return val.toFixed(1)
      },
    },
  },
})

/* ------------ Methods  ------------ */

async function showFirstSeries() {
  if (apexChart.value && series.value.length > 0) {
    try {
      apexChart.value.showSeries(series.value[0].name)
    } catch (error) {
      toast.error(getErrorMessage(error), 'خطا')
    }
  }
}

async function hideAllSeries() {
  if (apexChart.value && series.value.length > 0) {
    series.value.forEach((s) => {
      try {
        apexChart.value.hideSeries(s.name)
      } catch (error) {
        toast.error(getErrorMessage(error), 'خطا')
      }
    })
  }
}

async function manageSeriesVisibility() {
  await hideAllSeries()
  await showFirstSeries()
}

async function getGrowthChartData(fromDate: string, toDate: string, type: 'years' | 'months' | 'days') {
  try {
    loading.value = true
    chartReady.value = false

    const res = await getGrowthChartDataAction(fromDate, toDate, type)

    series.value = Object.values(res.data).map((item: any) => ({
      name: item.name,
      data: item.data,
      color: item.color,
    }))

    chartReady.value = true
    await nextTick()

    setTimeout(() => {
      manageSeriesVisibility()
    }, 300)
  } catch (error) {
    toast.error(getErrorMessage(error), 'خطا')
  } finally {
    loading.value = false
  }
}

async function getSeries() {
  try {
    loading.value = true
    const res = await getGrowthChartTitlesAction()
    series.value = Object.values(res.data).map((item: any) => ({
      name: item.name,
      data: item.data,
      color: item.color,
    }))

    chartReady.value = true
    await nextTick()

    setTimeout(() => {
      manageSeriesVisibility()
    }, 300)
  } catch (error) {
    toast.error(getErrorMessage(error), 'خطا')
  } finally {
    loading.value = false
  }
}

async function getDateYear() {
  try {
    const res = await getGrowthChartDateRangeAction()
    const dateRange = res.data[0]

    startYear.value = dateRange.start.year
    startMonth.value = dateRange.start.month
    startDay.value = dateRange.start.day

    currentYear.value = dateRange.current.year
    currentMonth.value = dateRange.current.month
    currentDay.value = dateRange.current.day

    if (yearOptions.value.length === 2) {
      const start = parseInt(startYear.value)
      const end = parseInt(currentYear.value)
      for (let year = start; year <= end; year++) {
        yearOptions.value.push({ label: String(year), value: String(year) })
      }
    }
  } catch (error) {
    toast.error(getErrorMessage(error), 'خطا')
  }
}

async function loadChartData() {
  await getDateYear()
  await getSeries()
}

async function getData(year: string | null, month: string | null) {
  let fromDate: string
  let toDate: string
  let type: 'years' | 'months' | 'days'

  if (year === null) {
    await loadChartData()
    return
  } else if (year === 'all') {
    fromDate = `${startYear.value}-${startMonth.value}-1`
    toDate = `${currentYear.value}-${currentMonth.value}-${currentDay.value}`
    type = 'years'
  } else if (month === 'all') {
    fromDate = `${year}-1-1`
    toDate = `${year}-12-31`
    type = 'months'
  } else {
    fromDate = `${year}-${month}-1`
    toDate = `${year}-${month}-31`
    type = 'days'
  }

  await getGrowthChartData(fromDate, toDate, type)
}

/* ------------ Watchers  ------------ */

watch(selectedYear, (newVal) => {
  if (newVal === 'all') {
    selectedMonth.value = null
  }
  getData(selectedYear.value, selectedMonth.value)
})

watch(selectedMonth, (newVal) => {
  getData(selectedYear.value, selectedMonth.value)
})

/* ------------ Lifecycle ------------ */

onMounted(() => {
  loadChartData()
})
</script>

<template>
  <div>
    <div v-if="loading" class="text-center py-10">در حال بارگذاری...</div>

    <apexchart v-else-if="chartReady && series.length > 0" ref="apexChart" type="line" height="480" :width="chartWidth"
      :options="chartOptions" :series="series" />

    <div v-else-if="chartReady && series.length === 0" class="text-center py-10">
      داده‌ای برای نمایش موجود نیست
    </div>

    <div class="w-full flex flex-col gap-8 select-chart-style mt-2">
      <Select variant="checkbox" v-model="selectedYear" class="w-full h-12!" label="سال را انتخاب کنید"
        :options="yearOptions" />
      <Select v-if="selectedYear && selectedYear !== 'all'" variant="checkbox" v-model="selectedMonth"
        class="w-full h-12!" label="ماه را انتخاب کنید" :options="monthOptions" />
    </div>
  </div>
</template>

<style>
.select-chart-style label,
.select-chart-style .bg-white {
  background-color: #EAEEF5 !important;
}
</style>